import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cx } from '../utils/cx.js';
import styles from './ScrollArea.module.css';

export type ScrollAreaOrientation = 'vertical' | 'horizontal' | 'both';

export type ScrollAreaProps = HTMLAttributes<HTMLDivElement> & {
  children: ReactNode;
  /** Cap on the outer frame. Viewport fills it and scrolls. */
  maxHeight?: string | number;
  /** Which axes scroll. Default vertical. */
  orientation?: ScrollAreaOrientation;
  /** Classes for the scrolling viewport (e.g. fade utilities). */
  viewportClassName?: string;
  /**
   * 48px edge dissolve when more content sits past that edge (FF scroll-fade).
   * Off by default — tables with sticky headers often prefer a hard clip.
   */
  fade?: boolean | 'x' | 'y' | 'both';
};

/**
 * Fluid Functionalism–style scroll region (ported; no Radix / shadcn).
 * Touch keeps native overflow. Fine-pointer thumbs come from useScrollbarReveal mist.
 */
export const ScrollArea = forwardRef<HTMLDivElement, ScrollAreaProps>(function ScrollArea(
  {
    maxHeight,
    className,
    style,
    children,
    orientation = 'vertical',
    viewportClassName,
    fade = false,
    ...rest
  },
  ref,
) {
  const mh =
    maxHeight == null ? undefined : typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight;

  const fadeClass =
    fade === true || fade === 'y'
      ? styles.fadeY
      : fade === 'x'
        ? styles.fadeX
        : fade === 'both'
          ? cx(styles.fadeY, styles.fadeX)
          : undefined;

  return (
    <div
      role="group"
      data-slot="scroll-area"
      aria-roledescription="scroll area"
      data-wp-scroll-area-root=""
      className={cx(styles.root, className)}
      style={{ ...(mh ? { maxHeight: mh } : {}), ...style }}
      {...rest}
    >
      <div
        ref={ref}
        data-slot="scroll-area-viewport"
        data-wp-scroll-area=""
        tabIndex={0}
        className={cx(
          styles.viewport,
          orientation === 'vertical' && styles.overflowY,
          orientation === 'horizontal' && styles.overflowX,
          orientation === 'both' && styles.overflowBoth,
          fadeClass,
          viewportClassName,
        )}
      >
        {children}
      </div>
    </div>
  );
});
